import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  LOCALE_ID,
  OnInit,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { AdminCart, CartFiltersApi } from '../../../../pages/admin-dashboard/cart-management/admin-cart.model';
import { CreateCartDialogComponent } from '../create-cart-dialog/create-cart-dialog';
import { CartService } from '../../../../services/cart/cart';
import { CartListFiltersComponent } from './cart-list-filters/cart-list-filters';
import { CurrentUserService } from '../../../../services/current-user.service';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { CartListTable, LoadState } from './cart-list-table/cart-list-table';

@Component({
  selector: 'app-admin-list-cart-component',
  standalone: true,
  imports: [
    CreateCartDialogComponent,
    CartListFiltersComponent,
    CartListTable,
    ConfirmationDialog,
    SuccessfulNotification,
  ],
  templateUrl: './admin-list-cart-component.html',
  styleUrl: './admin-list-cart-component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminListCartComponent implements OnInit {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);
  private cartService = inject(CartService);
  private destroyRef = inject(DestroyRef);
  private currentUserService = inject(CurrentUserService);
  private platformId = inject(PLATFORM_ID);

  state = signal<LoadState>('loading');
  carts = signal<AdminCart[] | null>(null);
  currentPage = signal(1); // 1-based
  itemsPerPage = signal(25);
  totalItems = signal(0);
  unAuthorized = signal(false);

  deleteLoading = signal(false);
  deleteError = signal('');
  deleteSuccess = signal(false);
  closeDeleteDialog = signal(0);

  showCreateDialog = signal(false);

  filters = signal<CartFiltersApi>({ orderBy: 'CREATION_DATE', orderDir: 'DESC' });

  isAr = computed(() => this.locale.startsWith('ar'));
  totalPages = computed(() => Math.ceil(this.totalItems() / this.itemsPerPage()) || 1);

  isFiltersApplied = computed(() => {
    const f = this.filters();
    return !!(
      f.cartTitle ||
      f.pharmacyId ||
      f.pharmacyOwnerId ||
      f.assignedToIds?.length ||
      f.createdOnFrom ||
      f.createdOnTo ||
      f.lastModifiedOnFrom ||
      f.lastModifiedOnTo
    );
  });

  ngOnInit() {
    this.loadCarts();
  }

  loadCarts() {
    this.filters.update((f) => ({
      ...f,
      pageNum: this.currentPage() - 1,
      pageSize: this.itemsPerPage(),
    }));
    this.state.set('loading');

    const sub = this.cartService.getAllCarts(this.filters()).subscribe({
      next: (res) => {
        this.carts.set(res.items);
        this.totalItems.set(res.total);
        this.state.set('success');
      },
      error: (err) => {
        if (err.status === 403 && isPlatformBrowser(this.platformId)) {
          this.unAuthorized.set(true);
          return;
        }
        this.state.set('error');
      },
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  onApplyFilters(applied: CartFiltersApi) {
    this.currentPage.set(1);
    this.filters.update((f) => ({ ...applied, orderBy: f.orderBy, orderDir: f.orderDir }));
    this.loadCarts();
  }

  onResetFilters() {
    this.currentPage.set(1);
    this.filters.set({ orderBy: 'CREATION_DATE', orderDir: 'DESC' });
    this.loadCarts();
  }

  onToggleSortOrder() {
    this.filters.update((f) => ({ ...f, orderDir: f.orderDir === 'ASC' ? 'DESC' : 'ASC' }));
    this.loadCarts();
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadCarts();
    }
  }

  onPageSizeChange(size: number) {
    this.itemsPerPage.set(size);
    this.currentPage.set(1);
    this.loadCarts();
  }

  onDeleteCart(id: number) {
    this.deleteLoading.set(true);
    this.deleteError.set('');

    const sub = this.cartService.deleteCart(id).subscribe({
      next: () => {
        this.deleteLoading.set(false);
        this.closeDeleteDialog.update((v) => v + 1);
        this.deleteSuccess.set(true);
        setTimeout(() => this.deleteSuccess.set(false), 3000);
        this.loadCarts();
      },
      error: (err) => {
        this.deleteLoading.set(false);
        this.deleteError.set(this.isAr() ? err.messageAr || 'حدث بعض الخطأ!' : err.messageEn || 'some error occurred!');
      },
    });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  onCheckoutCart(id: number) {
    // TODO: checkout API
  }

  openCreateCartDialog() {
    this.showCreateDialog.set(true);
  }
  onCartCreated(_newId: number) {
    this.showCreateDialog.set(false);
    this.loadCarts();
  }
  onCreateDialogClosed() {
    this.showCreateDialog.set(false);
  }

  viewCartDetails(id: number) {
    this.router.navigate(['/admin/cart/view-cart', id]);
  }

  redirectToLogin() {
    this.router.navigate(['/login']);
    this.currentUserService.logout();
  }
}
